import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { useCurrency } from '../hooks/useCurrency';
import { useAuthCtx } from '../App';
import { Icon } from '../components/ui/Icon';

const GIFT_THRESHOLD_CENTS = 30000; // $300

export default function ThankYouPage() {
  const { id } = useParams();
  const { fmt } = useCurrency();
  const { user, refreshUser } = useAuthCtx();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  // Pull the fresh wallet balance once the order settles so the Glow Cash reveal
  // can show the new total (the 1% is credited server-side at payment).
  const refreshedRef = useRef(false);

  // The Stripe webhook is the source of truth for "paid", and may land a moment
  // after the shopper is redirected here — poll until the status settles.
  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/orders/${id}`);
        setOrder(res.data);
        const settled = ['paid', 'processing', 'shipped', 'delivered'].includes(res.data.status);
        if (settled && !refreshedRef.current) {
          refreshedRef.current = true;
          refreshUser?.();
        }
        if (!settled && tries < 8) { tries += 1; timer = setTimeout(poll, 2000); }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load order');
      }
    };
    poll();
    return () => clearTimeout(timer);
    // refreshUser is a stable useCallback; re-running this poll on its identity
    // isn't wanted — only the order id drives it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isPaid = order && ['paid', 'processing', 'shipped', 'delivered'].includes(order.status);

  return (
    <div className="relative min-h-screen text-white overflow-hidden" style={{ background: 'radial-gradient(ellipse at top, #1a1410 0%, #0a0805 55%, #050302 100%)' }}>
      {/* Ambient gold */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[900px] rounded-full bg-amber-500/15 blur-[160px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-amber-700/10 blur-[120px]" />

      {/* Top bar */}
      <div className="relative border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/shop" className="flex items-center gap-2 text-amber-200 hover:text-amber-100 transition-colors">
            <span className="text-2xl">✦</span>
            <span className="font-light tracking-[0.3em] uppercase text-sm">AB8 Beauty</span>
          </Link>
          <span className="text-[10px] uppercase tracking-[0.4em] text-white/40">Order received</span>
        </div>
      </div>

      <main className="relative max-w-3xl mx-auto px-6 py-20">
        {/* Crest mark */}
        <div className="flex items-center justify-center mb-10">
          <div className="relative">
            <div className="absolute -inset-8 bg-amber-400/20 blur-3xl rounded-full" />
            <div className="relative h-24 w-24 rounded-full border border-amber-300/50 flex items-center justify-center bg-gradient-to-br from-amber-200/10 to-amber-700/10 backdrop-blur">
              <svg viewBox="0 0 24 24" className="w-10 h-10 text-amber-200" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 12.5l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>

        <h1 className="text-center font-light text-5xl md:text-6xl tracking-tight text-white mb-4">
          Thank you.
        </h1>
        <p className="text-center text-amber-200/80 text-base tracking-wide max-w-xl mx-auto leading-relaxed mb-2">
          {isPaid ? 'Payment confirmed — your order is in our hands.' : 'Confirming your payment…'}
        </p>
        <p className="text-center text-white/60 text-sm max-w-xl mx-auto leading-relaxed">
          Every order at AB8 Beauty is handled personally — verified, packaged, and shipped with the
          discretion you would expect from a private concierge. You will hear from us shortly with a tracking number.
        </p>

        {order && (
          <div className="mt-10 flex items-center justify-center gap-4 text-sm">
            <span className="px-4 py-1.5 rounded-full border border-amber-300/30 text-amber-200/90 text-[10px] uppercase tracking-[0.3em]">
              Order #{order.id.slice(0, 8).toUpperCase()}
            </span>
            <span className="text-white/40">·</span>
            <span className="text-white">{fmt(order.totalInCents)}</span>
          </div>
        )}

        {/* Glow Cash reveal — the buyer's 1% reward, credited at payment */}
        {order && order.rewardEarnedInCents > 0 && (
          <div className="mt-12 relative overflow-hidden rounded-3xl border border-amber-300/40 p-8 text-center" style={{ background: 'radial-gradient(ellipse at top, rgba(222,194,122,0.22), transparent 70%)' }}>
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 h-44 w-44 rounded-full bg-amber-400/30 blur-3xl animate-pulse" />
            <div className="relative">
              <div className="mb-2 flex justify-center animate-bounce text-amber-300" aria-hidden="true"><Icon name="commissions" className="w-10 h-10" /></div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300 mb-3">You just earned</div>
              <div className="text-5xl md:text-6xl font-light text-amber-100 mb-3 tracking-tight">
                {fmt(order.rewardEarnedInCents)}
              </div>
              <div className="text-amber-200/90 text-sm tracking-[0.25em] uppercase mb-4">Glow Cash</div>
              <p className="text-white/70 max-w-md mx-auto text-sm leading-relaxed">
                That's <strong className="text-amber-100">1% of your order</strong> dropped straight into your wallet —
                yours to spend on your next AB8 Beauty haul.
              </p>
              {user?.walletBalanceCents !== undefined && (
                <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-amber-300/30 bg-white/[0.04] px-6 py-2.5 backdrop-blur">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">Wallet balance</span>
                  <span className="text-amber-100 font-semibold text-lg">{fmt(user.walletBalanceCents)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Three-pillar reassurance */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { glyph: '✦', title: 'Personally verified', body: 'Every shipment is inspected by hand before it leaves our facility.' },
            { glyph: '◇', title: 'Discreet packaging', body: 'Unbranded, sealed, and shipped to your address without disclosure.' },
            { glyph: '⌭', title: 'Concierge support', body: 'A single point of contact for tracking, queries, and aftercare.' },
          ].map(p => (
            <div key={p.title} className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-6">
              <div className="text-amber-200 text-2xl mb-3">{p.glyph}</div>
              <div className="text-white font-medium mb-1">{p.title}</div>
              <div className="text-xs text-white/55 leading-relaxed">{p.body}</div>
            </div>
          ))}
        </div>

        {/* $300 surprise gift */}
        {order && order.totalInCents >= GIFT_THRESHOLD_CENTS && (
          <div className="mt-12 relative overflow-hidden rounded-3xl border border-amber-300/30 p-8 text-center" style={{ background: 'radial-gradient(ellipse at top, rgba(222,194,122,0.18), transparent 70%)' }}>
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 h-40 w-40 rounded-full bg-amber-400/30 blur-3xl" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300 mb-3">A token of our gratitude</div>
              <div className="text-3xl font-light text-amber-100 mb-3">A surprise gift travels with your order</div>
              <p className="text-white/70 max-w-lg mx-auto text-sm leading-relaxed">
                Orders above {fmt(GIFT_THRESHOLD_CENTS)} are accompanied by a complimentary item, chosen personally by our team.
                Consider it our quiet way of saying thank you.
              </p>
            </div>
          </div>
        )}

        {/* Footer CTAs */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/account"
            className="px-8 py-3 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-black font-semibold tracking-wide hover:shadow-2xl hover:shadow-amber-500/30 transition-all"
          >
            View my orders
          </Link>
          <Link
            to="/shop"
            className="px-8 py-3 rounded-full border border-amber-300/40 text-amber-200 hover:bg-amber-300/10 transition-colors"
          >
            Continue shopping
          </Link>
        </div>

        {error && (
          <p className="mt-10 text-center text-red-300/80 text-sm">{error}</p>
        )}
      </main>

      <footer className="relative border-t border-white/5 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-6 text-center text-[10px] uppercase tracking-[0.4em] text-white/30">
          With our gratitude · AB8 Beauty
        </div>
      </footer>
    </div>
  );
}
